/**
 * One-ladder participant label: team name → alias → script-aware personal name.
 *
 * This is the seed of a web-wide consolidation (see CHANGELOG). The app side
 * already has this ladder (`resolveDisplayName` in playpulse-app); this module
 * is the web counterpart for one call site at a time — it does not yet replace
 * every place that names a participant, only new/updated ones that opt in.
 *
 * Ladder:
 *   1. Team rung — a team-division entry's typed team name (never the
 *      registrant's own "First Last" stored as a default; see `teamLabel` /
 *      `resolveTeamEntry` in `teamLabels.ts`, which this module never
 *      reimplements).
 *   2. Alias rung — a trimmed, non-empty `player.alias` (an individual who set
 *      an alias is called by it everywhere; product ruling 2026-09-21).
 *   3. Personal name — `playerModelV2.formatName`, script-aware (CJK
 *      last+first, Latin first+last, optional short/maxLength forms).
 *   4. `''` when there is no player to name at all.
 *
 * Team and alias are ellipsis-truncated with `truncateLabel` when `maxLength`
 * is given — never abbreviated the way a personal name can be ("F. Surname").
 */

import { playerModelV2 } from '@/models/playerModelV2';
import { nameSlashBreaks } from './nameBreaks';
import { teamLabel, truncateLabel, type TeamEntry } from './teamLabels';

export type ParticipantLabelPlayer = {
  first_name: string | null;
  last_name: string | null;
  alias?: string | null;
};

export function resolveParticipantLabel({
  entry,
  player,
  isJapanese,
  form = 'full',
  maxLength,
}: {
  entry?: TeamEntry | null;
  player: ParticipantLabelPlayer | null | undefined;
  isJapanese: boolean;
  form?: 'full' | 'short';
  maxLength?: number;
}): string {
  const team = teamLabel(entry, '').trim();
  if (team) return truncateLabel(team, maxLength);

  if (!player) return '';

  const alias = (player.alias ?? '').trim();
  if (alias) return truncateLabel(alias, maxLength);

  // No team, no alias, no name parts: '' — the app's resolver answers its own
  // fallback here, and the app renders these labels verbatim (the scorer seed
  // burns them into the scorebug), so formatName's literal 'Unknown Player'
  // must never be the answer. Guarded on the input, not the output, so a
  // player really named "Unknown Player" keeps their name.
  if (!(player.first_name ?? '').trim() && !(player.last_name ?? '').trim()) return '';

  return playerModelV2.formatName(player.first_name, player.last_name, null, {
    isJapanese,
    short: form === 'short',
    maxLength,
  });
}

export function resolveParticipantInitials({
  entry,
  player,
}: {
  entry?: TeamEntry | null;
  player: ParticipantLabelPlayer | null | undefined;
}): string {
  const team = teamLabel(entry, '').trim();
  if (team) {
    const words = team.split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) {
      return (playerModelV2.safeFirstChar(words[0]) || '?').toUpperCase();
    }
    const first = playerModelV2.safeFirstChar(words[0]);
    const second = playerModelV2.safeFirstChar(words[1]);
    return (first + second || '?').toUpperCase();
  }

  if (!player) return '?';

  const alias = (player.alias ?? '').trim();
  if (alias) return playerModelV2.getInitials(null, null, alias);

  return playerModelV2.getInitials(player.first_name, player.last_name);
}

const PAIR_SEPARATOR = /\s+[&+/／]\s+/u;

/**
 * Two-character initials for a display name that is already resolved (team, alias or personal
 * name, in whatever form the row shows). Web-only: the shared ladder above takes player objects,
 * and this is for callers (the Watch panel) that only hold the gated name string. A pair
 * ("A / B", "A & B", "HKFC/Stellar") reads one letter per partner; a spaced name its first two
 * words; one word its first two characters. Not part of the shared case table.
 */
export function initialsFromLabel(label: string): string {
  const text = label.trim();
  if (!text) return '?';
  const first = (word: string) => playerModelV2.safeFirstChar(word);

  const spaced = text.split(PAIR_SEPARATOR);
  const partners = (
    spaced.length > 1 ? spaced : nameSlashBreaks(text).map((part) => part.replace(/[/／]$/u, ''))
  )
    .map((part) => part.trim())
    .filter(Boolean);
  if (partners.length > 1) {
    return (first(partners[0]!) + first(partners[1]!)).toUpperCase();
  }

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length > 1) return (first(words[0]!) + first(words[1]!)).toUpperCase();
  return Array.from(text).slice(0, 2).join('').toUpperCase();
}
